import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Hook for auto-submission when time expires
 * @param {object} options - Configuration options
 * @param {object} options.data - Question data
 * @param {number} options.selectedLanguage - Selected language ID
 * @param {string} options.timeRemaining - Remaining time (string format like "01:23")
 * @param {string} options.code - Current code
 * @param {function} options.handleNext - Function to handle next/submit
 * @param {function} options.autoSaveCode - Function to auto-save code
 * @param {function} options.showModal - Function to show modal
 * @param {function} options.closeModal - Function to close modal
 * @param {string} options.skillName - Skill name
 * @param {object} options.eventBufferRef - Event buffer ref
 * @returns {object} Auto-submission state
 */
export const useAutoSubmission = ({
  data,
  selectedLanguage,
  timeRemaining,
  code,
  handleNext,
  autoSaveCode,
  showModal,
  closeModal,
  skillName,
  eventBufferRef,
}) => {
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionCompleted, setSubmissionCompleted] = useState(false);
  const isSubmittingRef = useRef(false);
  const hasAutoSubmittedRef = useRef(false);
  const submissionCompletedRef = useRef(false);
  const modalCloseTimeoutRef = useRef(null);

  /**
   * Parse time string to total seconds
   */
  const parseTimeToSeconds = useCallback((timeStr) => {
    if (typeof timeStr === "string") {
      const parts = timeStr.split(":");
      if (parts.length === 2) {
        const minutes = parseInt(parts[0], 10) || 0;
        const seconds = parseInt(parts[1], 10) || 0;
        return minutes * 60 + seconds;
      }
    } else if (typeof timeStr === "number") {
      return timeStr;
    }
    return 0;
  }, []);

  /**
   * Check if time has expired and conditions are met for auto-submission
   * Uses refs to avoid stale closure issues
   */
  const shouldAutoSubmit = useCallback(() => {
    if (typeof timeRemaining === "string") {
      const totalSeconds = parseTimeToSeconds(timeRemaining);
      return (
        totalSeconds === 0 &&
        !isSubmittingRef.current &&
        !hasAutoSubmittedRef.current &&
        code &&
        selectedLanguage &&
        handleNext
      );
    }
    return false;
  }, [timeRemaining, code, selectedLanguage, handleNext, parseTimeToSeconds]);

  /**
   * Close modal with cleanup
   */
  const closeModalSafely = useCallback(() => {
    // Clear any existing timeout
    if (modalCloseTimeoutRef.current) {
      clearTimeout(modalCloseTimeoutRef.current);
      modalCloseTimeoutRef.current = null;
    }
    closeModal();
  }, [closeModal]);

  /**
   * Perform auto-submission
   */
  const performAutoSubmission = useCallback(async () => {
    if (!handleNext || !code || !selectedLanguage) {
      console.warn("⚠️ Auto-submission skipped: missing required data");
      return;
    }

    // Prevent multiple submissions
    if (isSubmittingRef.current) {
      console.warn("⚠️ Auto-submission already in progress");
      return;
    }

    // Set submitting state
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    submissionCompletedRef.current = false;
    setSubmissionCompleted(false);

    try {
      // Auto-save final code before submission
      await autoSaveCode();

      // Submit the code
      await handleNext(
        {
          sourceCode: code,
          programmingLanguageId: selectedLanguage,
          editorEvents: eventBufferRef?.current || [],
          candidateAnswer: code,
          type: "programming",
          skill: skillName,
        },
        true
      ); // true indicates auto-submit

      // Mark submission as completed (both state and ref)
      submissionCompletedRef.current = true;
      setSubmissionCompleted(true);

      // Clear any existing timeout
      if (modalCloseTimeoutRef.current) {
        clearTimeout(modalCloseTimeoutRef.current);
      }

      // Auto-close modal after 2 seconds
      modalCloseTimeoutRef.current = setTimeout(() => {
        closeModalSafely();
        modalCloseTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error("❌ Auto-submission failed:", error);
      submissionCompletedRef.current = true;
      setSubmissionCompleted(true);

      // Clear any existing timeout
      if (modalCloseTimeoutRef.current) {
        clearTimeout(modalCloseTimeoutRef.current);
      }

      showModal(
        "Auto-Submission Failed",
        "There was an issue submitting your code automatically. Please contact support.",
        "error",
        {
          icon: "/assets/error.svg",
          showFooter: true,
        }
      );
    } finally {
      // Always reset submission state
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  }, [
    code,
    selectedLanguage,
    handleNext,
    autoSaveCode,
    skillName,
    eventBufferRef,
    closeModalSafely,
    showModal,
  ]);

  // Sync ref with state
  useEffect(() => {
    hasAutoSubmittedRef.current = hasAutoSubmitted;
  }, [hasAutoSubmitted]);

  // Auto-submit when time expires (with improved logic to prevent multiple calls)
  useEffect(() => {
    if (shouldAutoSubmit()) {
      hasAutoSubmittedRef.current = true;
      setHasAutoSubmitted(true); // Prevent multiple calls

      // Show auto-submission modal
      showModal(
        "Time's Up!",
        "Your time has expired. Your code is being automatically submitted...",
        "loading",
        {
          showFooter: false, // Hide footer during auto-submission
          icon: "/assets/clock.svg",
        }
      );

      // Use setTimeout for reliable execution
      setTimeout(() => {
        performAutoSubmission();
      }, 100); // Small delay to ensure state is properly set
    }
  }, [timeRemaining, shouldAutoSubmit, showModal, performAutoSubmission]);

  // Backup auto-submission check to ensure it works even if timer fails
  // More frequent check (every 1 second) to catch time expiration reliably
  useEffect(() => {
    if (!data || !selectedLanguage || hasAutoSubmittedRef.current) return;

    const backupCheckInterval = setInterval(() => {
      if (shouldAutoSubmit()) {
        hasAutoSubmittedRef.current = true;
        setHasAutoSubmitted(true);

        showModal(
          "Time's Up!",
          "Your time has expired. Your code is being automatically submitted...",
          "loading",
          {
            showFooter: false,
            icon: "/assets/clock.svg",
          }
        );

        setTimeout(() => {
          performAutoSubmission();
        }, 100);
      }
    }, 1000); // Check every 1 second as backup (more frequent for reliability)

    return () => clearInterval(backupCheckInterval);
  }, [
    data,
    selectedLanguage,
    shouldAutoSubmit,
    showModal,
    performAutoSubmission,
  ]);

  // ✅ CRITICAL FIX: Handle tab visibility changes
  // When user returns to tab, check if time has expired and submit if needed
  // Also close modal if submission completed while tab was hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - check if submission completed while tab was hidden
        if (submissionCompletedRef.current) {
          // Close modal immediately if submission completed
          closeModalSafely();
          return;
        }

        // Tab became visible - check if time has expired

        // Use a small delay to ensure timeRemaining prop is up-to-date
        // Also check directly using parseTimeToSeconds to avoid stale closure issues
        setTimeout(() => {
          // Check directly if time has expired (more reliable than using callback)
          const totalSeconds = parseTimeToSeconds(timeRemaining);
          const canSubmit =
            totalSeconds === 0 &&
            !isSubmittingRef.current &&
            !hasAutoSubmittedRef.current &&
            code &&
            selectedLanguage &&
            handleNext;

          if (canSubmit) {
            hasAutoSubmittedRef.current = true;
            setHasAutoSubmitted(true);

            showModal(
              "Time's Up!",
              "Your time has expired while you were away. Your code is being automatically submitted...",
              "loading",
              {
                showFooter: false,
                icon: "/assets/clock.svg",
              }
            );

            setTimeout(() => {
              performAutoSubmission();
            }, 100);
          }
        }, 200); // Small delay to ensure state is synced
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Cleanup timeout on unmount
      if (modalCloseTimeoutRef.current) {
        clearTimeout(modalCloseTimeoutRef.current);
        modalCloseTimeoutRef.current = null;
      }
    };
  }, [
    timeRemaining,
    code,
    selectedLanguage,
    handleNext,
    parseTimeToSeconds,
    showModal,
    performAutoSubmission,
    closeModalSafely,
  ]);

  // ✅ FIX: Fallback mechanism to close modal if it's stuck open
  // This ensures the modal closes even if the timeout didn't fire while tab was hidden
  // Uses a periodic check that runs when submission is completed
  useEffect(() => {
    if (!submissionCompleted) return;

    // Set up a periodic check to close modal if it's still open
    // This handles cases where the timeout didn't fire (e.g., tab was hidden)
    const checkInterval = setInterval(() => {
      // Check if submission completed and close modal
      // The timeout should have closed it, but if tab was hidden, it might not have fired
      if (submissionCompletedRef.current) {
        closeModalSafely();
        // Clear interval after first check (we only need to check once)
        clearInterval(checkInterval);
      }
    }, 500); // Check every 500ms for faster response

    // Also set a maximum timeout to ensure modal closes (5 seconds max)
    const maxTimeout = setTimeout(() => {
      closeModalSafely();
      clearInterval(checkInterval);
    }, 5000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(maxTimeout);
    };
  }, [submissionCompleted, closeModalSafely]);

  return {
    hasAutoSubmitted,
    setHasAutoSubmitted,
    isSubmitting,
    setIsSubmitting,
    isSubmittingRef,
  };
};
